/**
 * Datadog RUM setup for portal-v2.
 */

import { AppConfig } from '@/appConfig';
import {
  classifyAngularRejection,
  dropHttpRejections,
  groupAngularBootFailure,
  initRum,
  redactAngularErrorParams,
  reportError,
  setRumUser,
} from '@ringba/telemetry/rum';

// Third-party and browser-quirk messages with no signal for us, matched against the error's message.
const V2_NOISE_PATTERNS = [
  'Unexpected token G in JSON at position 0',
  'Unexpected token  in JSON at position 0',
  'Unexpected token b in JSON at position 1',
  'The operation was aborted.',
  'The fetching process for the media resource was aborted by the user agent at the user\'s request.',
  '$digest already in progress',
  'The play() request was interrupted',
  'The element has no supported sources.',
  'Failed to load because no supported source was found.',
  'ResizeObserver loop completed with undelivered notifications',
  'Export \'prism_bash_exports\' is not defined in module',
  'Exported binding \'prism_json_exports\' needs to refer to a top-level declared variable.',
  'Exported binding \'prism_python_exports\' needs to refer to a top-level declared variable.',
  'Flag configuration fetch superseded by a newer context update',
  'https://goo.gl/LdLk22',
  'top.GLOBALS',
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  'Can\'t find variable: ZiteReader',
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  'fb_xd_fragment',
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  'conduitPage',
  'Script error.',
];

// Third-party and browser-extension scripts, matched against the error's stack.
const V2_DENY_SCRIPT_URLS = [
  /cdn\.statuspage\.io\/se-v2\.js/i,
  /graph\.facebook\.com/i,
  /connect\.facebook\.net\/en_US\/all\.js/i,
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  /extensions\//i,
  /chrome:\/\//i,
  /127\.0\.0\.1:4001\/isrunning/i,
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  /client\.crisp\.chat\//i,
];

initRum(AppConfig.datadog, {
  noisePatterns: V2_NOISE_PATTERNS,
  denyScriptUrlPatterns: V2_DENY_SCRIPT_URLS,
  // AngularJS's `$exceptionHandler` logs through `$log.error`, so every exception `captureException` reports is also collected from the console.
  dropConsoleErrors: true,
  beforeSend: [dropHttpRejections(), redactAngularErrorParams(), groupAngularBootFailure()],
});

// Reporting API for AngularJS code, which is not bundled and cannot import.
// An SDK-recognisable global name would let page scripts and extensions detect, reuse or re-init our client.
// Every member swallows its failures: telemetry must never break the caller.
window.__ringbaErrorReporter = {
  captureException(error, context) {
    try {
      const verdict = classifyAngularRejection(error);
      if (verdict.action === 'drop') return;
      reportError(verdict.action === 'rewrite' ? new Error(verdict.message) : error, context);
    } catch {
      // noop
    }
  },

  /** Takes the login itself; the address is hashed before it reaches Datadog. */
  setUser(login, accountId) {
    try {
      setRumUser({
        accountId: accountId == null
          ? null
          : String(accountId),
        userLogin: login,
      });
    } catch {
      // noop
    }
  },
};
